import type { RuntimeConfig } from "./config";
import {
  getClientPlatform,
  invokeNative,
  isTauri,
  localPathForDisplay,
  openLocalPath,
  revealLocalPath,
} from "./platform";
import type { ArtifactManifest, RemoteMediaAsset, SaveResult } from "./globals";

const HANDLE_DATABASE = "bianhua-artifact-handles";
const HANDLE_STORE = "directories";
const HANDLE_KEY = "default-project-root";
const MAX_WEB_BYTES = 512 * 1024 * 1024;

type NativeSaveResponse = {
  disposition: "created" | "reused" | "versioned";
  absolutePath: string;
  relativePath: string;
  size: number;
  sha256: string;
};

type PreparedArtifactExport = {
  projectName: string;
  relativePath: string;
  suggestedFileName: string;
  mimeType: string;
  sha256: string;
  size: number;
  picker: "android-saf-create-document" | "system-save-dialog";
  requiresUserGesture: true;
};

export type ArtifactExportReference = {
  artifactId?: string;
  relativePath?: string;
  sha256?: string;
  mimeType?: string;
};

export type ArtifactExportResult =
  | { status: "cancelled" }
  | {
    status: "exported";
    destinationUri: string;
    relativePath: string;
    sha256: string;
    size: number;
    verified: true;
  };

type NativeArtifactRecord = {
  relativePath: string;
  sha256: string;
  size: number;
  mimeType?: string;
};

function extensionFor(mime = "application/octet-stream"): string {
  return ({
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "image/svg+xml": "svg",
    "application/zip": "zip",
    "application/pdf": "pdf",
  } as Record<string, string>)[mime] || "bin";
}

function cleanSegment(segment: string): string {
  const cleaned = segment.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-").replace(/[. ]+$/g, "").trim();
  if (!cleaned || cleaned === "." || cleaned === "..") throw new Error("产物路径包含无效名称");
  return cleaned.slice(0, 120);
}

function validateRelativeSegment(segment: string): string {
  if (/[<>:"/\\|?*\u0000-\u001f]/.test(segment) || /[. ]$/.test(segment)) {
    throw new Error("服务器产物路径包含 Windows 不允许的字符");
  }
  const stem = segment.split(".")[0].toUpperCase();
  if (["CON", "PRN", "AUX", "NUL"].includes(stem) || /^(COM|LPT)[1-9]$/.test(stem)) {
    throw new Error("服务器产物路径使用了 Windows 保留名");
  }
  return segment;
}

function safeRelativePath(input: string): string[] {
  if (!input || /^[a-zA-Z]:[\\/]/.test(input) || input.startsWith("/") || input.startsWith("\\")) {
    throw new Error("服务器产物必须使用工作区相对路径");
  }
  const parts = input.replace(/\\/g, "/").split("/");
  if (parts.some((part) => !part || part === "." || part === "..")) throw new Error("服务器产物路径不安全");
  return parts.map(validateRelativeSegment);
}

async function digestHex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

function openHandleDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(HANDLE_DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(HANDLE_STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  const database = await openHandleDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(HANDLE_STORE, "readwrite");
    transaction.objectStore(HANDLE_STORE).put(handle, HANDLE_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

async function readHandle(): Promise<FileSystemDirectoryHandle | null> {
  if (!("indexedDB" in window)) return null;
  const database = await openHandleDatabase();
  const result = await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
    const transaction = database.transaction(HANDLE_STORE, "readonly");
    const request = transaction.objectStore(HANDLE_STORE).get(HANDLE_KEY);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return result;
}

async function hasWritePermission(handle: FileSystemDirectoryHandle, request: boolean): Promise<boolean> {
  if (await handle.queryPermission?.({ mode: "readwrite" }) === "granted") return true;
  return request && await handle.requestPermission?.({ mode: "readwrite" }) === "granted";
}

async function writeWebFile(root: FileSystemDirectoryHandle, relativePath: string, blob: Blob): Promise<SaveResult> {
  if (blob.size > MAX_WEB_BYTES) throw new Error("网页端单个产物不能超过 512 MB，请使用 Windows 客户端同步");
  const segments = safeRelativePath(relativePath);
  let directory = root;
  for (const segment of segments.slice(0, -1)) directory = await directory.getDirectoryHandle(segment, { create: true });
  const requestedName = segments.at(-1)!;
  const requested = requestedName.match(/^(.*?)(\.[^.]+)?$/);
  const stem = requested?.[1] || requestedName;
  const extension = requested?.[2] || "";
  const incomingSha = await digestHex(await blob.arrayBuffer());
  let selectedName = requestedName;
  let file: FileSystemFileHandle | null = null;
  for (let version = 1; version <= 1_000; version += 1) {
    selectedName = version === 1 ? requestedName : `${stem} (${version})${extension}`;
    try {
      const existing = await directory.getFileHandle(selectedName);
      const existingFile = await existing.getFile();
      if (existingFile.size === blob.size && await digestHex(await existingFile.arrayBuffer()) === incomingSha) {
        return { status: "unchanged", path: [...segments.slice(0, -1), selectedName].join("/"), bytes: blob.size, sha256: incomingSha };
      }
    } catch (error) {
      if (error instanceof DOMException && error.name !== "NotFoundError") throw error;
      file = await directory.getFileHandle(selectedName, { create: true });
      break;
    }
  }
  if (!file) throw new Error("同名网页产物版本超过 1000 个");
  const writable = await file.createWritable({ keepExistingData: false });
  try {
    await writable.write(blob);
    await writable.close();
  } catch (error) {
    await writable.abort(error).catch(() => {});
    throw error;
  }
  return { status: "saved", path: [...segments.slice(0, -1), selectedName].join("/"), bytes: blob.size, sha256: incomingSha };
}

async function fetchWebArtifact(url: string, allowedHosts: string[], expectedSize?: number): Promise<Blob> {
  const parsed = new URL(url);
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) {
    throw new Error("网页端只接受不含凭据的 HTTPS 产物地址");
  }
  if (!allowedHosts.includes(parsed.hostname.toLowerCase())) {
    throw new Error("产物下载域名不在网页端允许列表中");
  }
  const response = await fetch(parsed, { credentials: "omit", redirect: "error" });
  if (!response.ok) throw new Error(`产物下载失败（${response.status}）`);
  const declaredLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredLength) && declaredLength > MAX_WEB_BYTES) {
    throw new Error("网页端单个产物不能超过 512 MB，请使用 Windows 客户端同步");
  }
  if (expectedSize !== undefined && Number.isFinite(declaredLength) && declaredLength !== expectedSize) {
    throw new Error("产物响应大小与服务器清单不一致");
  }
  if (!response.body) return response.blob();
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    received += value.byteLength;
    if (received > MAX_WEB_BYTES || (expectedSize !== undefined && received > expectedSize)) {
      await reader.cancel("artifact size limit exceeded").catch(() => {});
      throw new Error("产物下载内容超过服务器清单或客户端大小上限");
    }
    chunks.push(value);
  }
  if (expectedSize !== undefined && received !== expectedSize) throw new Error("产物下载大小与服务器清单不一致");
  return new Blob(chunks as BlobPart[], { type: response.headers.get("content-type") || "application/octet-stream" });
}

function normalizeNativeResult(result: NativeSaveResponse): SaveResult {
  return {
    status: result.disposition === "reused" ? "unchanged" : "saved",
    path: result.absolutePath,
    bytes: result.size,
    sha256: result.sha256,
  };
}

export class ArtifactSync extends EventTarget {
  private readonly config: RuntimeConfig;
  private directoryHandle: FileSystemDirectoryHandle | null = null;
  private lastMediaAsset: RemoteMediaAsset | null = null;
  private savedAssets = new Set<string>();
  private nativeArtifacts = new Map<string, NativeArtifactRecord>();

  constructor(config: RuntimeConfig) {
    super();
    this.config = config;
  }

  async initialize(): Promise<void> {
    if (!isTauri()) this.directoryHandle = await readHandle().catch(() => null);
    window.addEventListener("bianhua:artifact-ready", (event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.asset) return;
      this.lastMediaAsset = detail.asset;
      if (isTauri() || this.directoryHandle) {
        this.saveMediaAsset(detail.asset).catch((error) => this.emit("error", { error }));
      } else {
        this.emit("permission-required", { artifactId: detail.asset.assetId });
      }
    });
  }

  async chooseWebDirectory(): Promise<boolean> {
    if (isTauri()) return true;
    if (!window.showDirectoryPicker) throw new Error("当前浏览器不支持文件夹授权，请使用下载按钮或 Windows 客户端");
    const handle = await window.showDirectoryPicker({ id: "bianhua-project-root", mode: "readwrite" });
    if (!await hasWritePermission(handle, true)) return false;
    this.directoryHandle = handle;
    await storeHandle(handle);
    this.emit("directory-ready", { name: handle.name });
    return true;
  }

  async saveMediaAsset(asset: RemoteMediaAsset, options: { force?: boolean } = {}): Promise<SaveResult> {
    this.lastMediaAsset = asset;
    if (!options.force && this.savedAssets.has(asset.assetId)) return { status: "unchanged" };
    const extension = extensionFor(asset.mimeType || asset.blob.type);
    const safeId = cleanSegment(String(asset.assetId || "server-image")).slice(0, 80);
    const relativePath = `images/bianhua-${safeId}.${extension}`;
    const bytes = asset.bytes || new Uint8Array(await asset.blob.arrayBuffer());
    const digestBytes = Uint8Array.from(bytes);
    const sha256 = asset.sha256 || await digestHex(digestBytes.buffer);
    let result: SaveResult;
    if (isTauri()) {
      const response = await invokeNative<NativeSaveResponse>("save_artifact_bytes", {
        request: {
          projectName: this.config.projectName,
          relativePath,
          bytes: Array.from(bytes),
          sha256,
          mimeType: asset.mimeType || asset.blob.type,
        },
      });
      result = normalizeNativeResult(response);
      this.rememberNativeArtifact(asset.assetId, response, asset.mimeType || asset.blob.type);
    } else {
      const handle = this.directoryHandle;
      if (!handle || !await hasWritePermission(handle, false)) return { status: "permission-required" };
      result = await writeWebFile(handle, relativePath, asset.blob);
    }
    this.savedAssets.add(asset.assetId);
    this.emit("saved", {
      artifactId: asset.assetId,
      result,
      mediaType: asset.mimeType || asset.blob.type,
    });
    return result;
  }

  async saveManifest(manifest: ArtifactManifest): Promise<SaveResult> {
    safeRelativePath(manifest.relativePath);
    if (manifest.sizeBytes !== undefined && manifest.sizeBytes > MAX_WEB_BYTES) throw new Error("单个产物超过客户端 512 MB 上限");
    if (isTauri() && manifest.download?.url) {
      if (!manifest.sha256) throw new Error("服务器产物清单缺少 SHA-256，已拒绝自动保存");
      const response = await invokeNative<NativeSaveResponse>("save_artifact_from_url", {
        request: {
          projectName: this.config.projectName,
          relativePath: manifest.relativePath,
          url: manifest.download.url,
          sha256: manifest.sha256,
          sizeBytes: manifest.sizeBytes,
        },
      });
      const result = normalizeNativeResult(response);
      this.rememberNativeArtifact(manifest.artifactId, response, manifest.mimeType);
      this.emit("saved", {
        artifactId: manifest.artifactId,
        result,
        mediaType: manifest.mimeType,
      });
      return result;
    }
    const handle = this.directoryHandle;
    let blob = manifest.blob;
    if (!blob && manifest.bytes) {
      blob = new Blob([manifest.bytes as BlobPart], manifest.mimeType ? { type: manifest.mimeType } : undefined);
    }
    if (!blob && manifest.download?.url) {
      blob = await fetchWebArtifact(manifest.download.url, this.config.artifactHosts, manifest.sizeBytes);
    }
    if (!blob) throw new Error("产物清单缺少可下载内容");
    if (manifest.sizeBytes !== undefined && blob.size !== manifest.sizeBytes) throw new Error("产物大小与服务器清单不一致");
    const buffer = await blob.arrayBuffer();
    const actual = await digestHex(buffer);
    if (manifest.sha256 && actual.toLowerCase() !== manifest.sha256.toLowerCase()) {
      throw new Error("产物 SHA-256 校验失败");
    }
    if (isTauri()) {
      const response = await invokeNative<NativeSaveResponse>("save_artifact_bytes", {
        request: {
          projectName: this.config.projectName,
          relativePath: manifest.relativePath,
          bytes: Array.from(new Uint8Array(buffer)),
          sha256: manifest.sha256 || actual,
          mimeType: manifest.mimeType,
        },
      });
      const result = normalizeNativeResult(response);
      this.rememberNativeArtifact(manifest.artifactId, response, manifest.mimeType || blob.type);
      this.emit("saved", {
        artifactId: manifest.artifactId,
        result,
        mediaType: manifest.mimeType || blob.type,
      });
      return result;
    }
    if (!handle || !await hasWritePermission(handle, false)) return { status: "permission-required" };
    const result = await writeWebFile(handle, manifest.relativePath, blob);
    this.emit("saved", {
      artifactId: manifest.artifactId,
      result,
      mediaType: manifest.mimeType || blob.type,
    });
    return result;
  }

  async exportSavedArtifact(reference: ArtifactExportReference): Promise<ArtifactExportResult> {
    if (!isTauri()) {
      throw new Error("系统文件保存器仅在原生客户端中可用");
    }
    const remembered = reference.artifactId ? this.nativeArtifacts.get(reference.artifactId) : undefined;
    const relativePath = reference.relativePath || remembered?.relativePath;
    const sha256 = reference.sha256 || remembered?.sha256;
    if (!relativePath || !sha256) {
      throw new Error("尚未找到已校验的应用私有产物，请等待原片保存完成后重试");
    }

    const mimeType = reference.mimeType || remembered?.mimeType;
    const prepared = await invokeNative<PreparedArtifactExport>("prepare_artifact_export", {
      request: {
        projectName: this.config.projectName,
        relativePath,
        sha256,
        ...(mimeType ? { mimeType } : {}),
      },
    });
    const platform = getClientPlatform();
    const expectedPicker = platform === "android"
      ? "android-saf-create-document"
      : "system-save-dialog";
    if (!prepared.requiresUserGesture || prepared.picker !== expectedPicker) {
      throw new Error("导出合同无效，已拒绝跳过系统文件保存器");
    }

    const extension = prepared.suggestedFileName.match(/\.([a-zA-Z0-9]{1,12})$/)?.[1];
    const { save } = await import("@tauri-apps/plugin-dialog");
    const destinationUri = await save({
      defaultPath: prepared.suggestedFileName,
      ...(extension ? { filters: [{ name: "彼岸花产物", extensions: [extension] }] } : {}),
    });
    if (!destinationUri) return { status: "cancelled" };

    const exported = await invokeNative<{
      destinationUri: string;
      relativePath: string;
      sha256: string;
      size: number;
      verified: boolean;
    }>(platform === "android" ? "export_artifact_to_uri" : "export_artifact_to_path", {
      request: {
        projectName: prepared.projectName,
        relativePath: prepared.relativePath,
        ...(platform === "android"
          ? { destinationUri }
          : { destinationPath: destinationUri }),
        sha256: prepared.sha256,
        sizeBytes: prepared.size,
      },
    });
    if (!exported.verified || exported.sha256 !== prepared.sha256 || exported.size !== prepared.size) {
      throw new Error("公共导出完成后校验失败");
    }
    return {
      status: "exported",
      destinationUri: exported.destinationUri,
      relativePath: exported.relativePath,
      sha256: exported.sha256,
      size: exported.size,
      verified: true,
    };
  }

  private rememberNativeArtifact(
    artifactId: string,
    response: NativeSaveResponse,
    mimeType?: string,
  ): void {
    this.nativeArtifacts.set(artifactId, {
      relativePath: response.relativePath,
      sha256: response.sha256,
      size: response.size,
      ...(mimeType ? { mimeType } : {}),
    });
  }

  private emit(name: string, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(name, { detail }));
  }
}

export async function installArtifactSync(config: RuntimeConfig): Promise<ArtifactSync> {
  const sync = new ArtifactSync(config);
  await sync.initialize();
  window.BianhuaArtifactSync = Object.freeze({
    saveMediaAsset: (asset, options) => sync.saveMediaAsset(asset, options),
    saveManifest: (manifest) => sync.saveManifest(manifest),
    exportSavedArtifact: (reference) => sync.exportSavedArtifact(reference),
    chooseWebDirectory: () => sync.chooseWebDirectory(),
    getPlatform: () => getClientPlatform(),
    displayLocalPath: (path) => localPathForDisplay(path),
    openLocalFile: (path) => openLocalPath(path),
    revealLocalFile: (path) => revealLocalPath(path),
  });
  return sync;
}
